import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Cube, Buildings, FilmStrip, Lightning } from '@phosphor-icons/react';
import { ParticleBackground } from '../components/ParticleBackground';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  const industries = [
    { icon: Cube, title: t('industry.surveyGeo'), desc: t('industry.surveyGeoDesc') },
    { icon: Buildings, title: t('industry.aec'), desc: t('industry.aecDesc') },
    { icon: FilmStrip, title: t('industry.film'), desc: t('industry.filmDesc') },
    { icon: Lightning, title: t('industry.energy'), desc: t('industry.energyDesc') },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        
        const [productsRes, caseStudiesRes] = await Promise.all([
          axios.get(`${API}/products?active_only=true`),
          axios.get(`${API}/case-studies?active_only=true`)
        ]);
        setProducts(productsRes.data.filter(p => p.product_type === 'featured'));
        setCaseStudies(caseStudiesRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <ParticleBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-xgrids-bg-primary/50 to-xgrids-bg-primary z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-6" data-testid="hero-subtitle">
              {t('home.heroSubtitle')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-title">
              {t('home.heroTitle1')}<br />
              <span className="text-xgrids-brand-primary">{t('home.heroTitle2')}</span>
            </h1>
            <p className="text-xgrids-text-secondary text-lg lg:text-xl max-w-2xl mb-10 leading-relaxed">
              {t('home.heroDesc')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/produk"
                className="btn-primary rounded-none inline-flex items-center gap-2 text-base"
                data-testid="hero-products-btn"
              >
                {t('home.viewProducts')}
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/tentang#contact"
                className="btn-outline rounded-none inline-flex items-center gap-2 text-base"
                data-testid="hero-contact-btn"
              >
                {t('nav.contactUs')}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border-2 border-xgrids-text-muted rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-3 bg-xgrids-brand-primary rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Products Section */}
      <section className="py-24 lg:py-32 bg-xgrids-bg-secondary" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('home.featuredProducts')}</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('home.featuredProductsSubtitle')}
            </h2>
            <p className="text-xgrids-text-secondary max-w-2xl">
              {t('home.featuredProductsDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group card-hover bg-xgrids-bg-surface rounded-sm overflow-hidden"
                data-testid={`product-card-${index}`}
              >
                <div className="aspect-video bg-black/50 overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-bold text-white mb-3">{product.name}</h3>
                  <p className="text-xgrids-text-secondary mb-4 line-clamp-2">{product.description}</p>
                  <p className="font-mono text-xgrids-brand-primary text-lg mb-6">
                    {t('nav.contactUs')}
                  </p>
                  <Link
                    to="/produk"
                    className="inline-flex items-center gap-2 text-xgrids-brand-primary hover:text-xgrids-brand-hover transition-colors"
                  >
                    {t('common.viewDetails')}
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 lg:py-32" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('home.industryApps')}</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('home.industryAppsSubtitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-hover bg-xgrids-bg-surface p-8 rounded-sm group"
                data-testid={`industry-card-${index}`}
              >
                <industry.icon 
                  size={40} 
                  weight="duotone" 
                  className="text-xgrids-brand-primary mb-6 group-hover:scale-110 transition-transform" 
                />
                <h3 className="font-heading text-lg font-bold text-white mb-3">{industry.title}</h3>
                <p className="text-xgrids-text-secondary text-sm">{industry.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/industri"
              className="btn-outline rounded-none inline-flex items-center gap-2"
              data-testid="industries-more-btn"
            >
              {t('home.learnMore')}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-24 lg:py-32 bg-xgrids-bg-secondary" data-testid="case-studies-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">Case Study</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('home.caseStudyTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="card-hover bg-xgrids-bg-surface rounded-sm overflow-hidden group"
                data-testid={`case-study-card-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={study.image_url}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="font-mono text-xgrids-brand-blue text-xs tracking-wider uppercase">
                    {study.industry}
                  </span>
                  <h3 className="font-heading text-lg font-bold text-white mt-2 mb-3">{study.title}</h3>
                  <p className="text-xgrids-text-secondary text-sm line-clamp-2">{study.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/case-study"
              className="btn-primary rounded-none inline-flex items-center gap-2"
              data-testid="case-studies-more-btn"
            >
              {t('home.viewAllCaseStudies')}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-xgrids-brand-primary/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-white mb-6">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-xgrids-text-secondary text-lg mb-10 max-w-2xl mx-auto">
              {t('home.ctaDesc')}
            </p>
            <Link
              to="/tentang#contact"
              className="btn-primary rounded-none inline-flex items-center gap-2 text-lg px-8 py-4"
              data-testid="cta-contact-btn"
            >
              {t('home.scheduleConsultation')}
              <ArrowRight size={22} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
